/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import {translate} from '@ion/core/src/locale/locale';
import {OfflineCanRequestResult, OfflineCanRequestStatus} from '@ion/core/src/types/services';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import Accordion from '@ion/dls/src/components/accordion/Accordion';
import StatusIndicator from '@ion/dls/src/components/status/StatusIndicator';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {IconStatus} from '@ion/dls/src/types/types';
import {
  canRequestSummaryAccordionExpanded,
  setCanRequestSummaryAccordionExpanded
} from './store/store';
import {
  CheckinConditionDisplayLabels,
  CheckinConditionType,
  formatCheckinConditionsForDisplay,
  getCheckinConditionTypeLabels,
  mapConditionTypesToLabels
} from './CanRequestCheckinConditionUtils';
import {formatCanNumberToHex, formatCanPayloadToHex} from './CanRequestUtils';

export interface CanRequestSummaryAccordionProps {
  requestDetails: OfflineCanRequestResult;
}

const CAN_REQUEST_STATUS_ICON_MAP: Record<OfflineCanRequestStatus, IconStatus> = {
  [OfflineCanRequestStatus.Pending]    : IconStatus.validation,
  [OfflineCanRequestStatus.Completed]  : IconStatus.active,
  [OfflineCanRequestStatus.Failed]     : IconStatus.issue,
  [OfflineCanRequestStatus.Cancelling] : IconStatus.validation,
  [OfflineCanRequestStatus.Cancelled]  : IconStatus.offline,
  [OfflineCanRequestStatus.Retrying]   : IconStatus.validation
};

const useStyles = makeStyles((theme) => ({
  metadataContainer : {
    width : '100%'
  },
  metadataItem : {
    flexBasis                      : '20%',
    maxWidth                       : '20%',
    [theme.breakpoints.down('sm')] : {
      flexBasis : '100%',
      maxWidth  : '100%'
    }
  },
  summaryAccordionWrapper : {
    marginBottom : '8px'
  },
  summaryAccordionTitle : {
    ...dlsAlt('typography', 'label3'),
    color      : dlsColor('grey2'),
    fontWeight : 700,
    fontSize   : '12px'
  },
  summaryAccordion : {
    backgroundColor  : dlsColor('grey5'),
    minHeight        : '40px !important',
    '&.Mui-expanded' : {
      minHeight : '40px !important'
    },
    '& .MuiAccordionSummary-content' : {
      margin : '0 !important'
    },
    '& .MuiAccordionSummary-expandIcon' : {
      color : dlsColor('grey2')
    }
  },
  sectionContainer : {
    marginTop                      : '16px',
    display                        : 'flex',
    flexWrap                       : 'wrap',
    justifyContent                 : 'space-between',
    width                          : '100%',
    [theme.breakpoints.down('sm')] : {
      flexDirection : 'column',
      rowGap        : '8px'
    }
  },
  sectionItem : {
    minWidth : 0
  },
  conditionsAppliedItem : {
    flexBasis : '100%',
    marginTop : '8px'
  },
  metadataLabel : {
    ...dlsAlt('typography', 'label2'),
    color    : dlsColor('grey2'),
    fontSize : '12px'
  },
  metadataValue : {
    ...dlsAlt('typography', 'label3'),
    color      : dlsColor('grey1'),
    fontWeight : 700,
    fontSize   : '14px',
    padding    : '8px 0 12px'
  },
  sectionDivider : {
    backgroundColor : dlsColor('grey4'),
    marginTop       : '8px',
    marginBottom    : '8px'
  },
  sectionLabel : {
    ...dlsAlt('typography', 'label2'),
    color    : dlsColor('grey2'),
    fontSize : '12px'
  },
  sectionValue : {
    ...dlsAlt('typography', 'label3'),
    color      : dlsColor('grey1'),
    fontWeight : 700,
    fontSize   : '14px',
    padding    : '8px 0 12px'
  },
  payloadValue : {
    ...dlsAlt('typography', 'label3'),
    color      : dlsColor('grey1'),
    fontWeight : 700,
    fontSize   : '14px',
    padding    : '8px 0 12px'
  }
}));

const getCheckinConditionDisplayLabels = (labels: Record<string, any>): CheckinConditionDisplayLabels => ({
  conditionTypeLabels : getCheckinConditionTypeLabels(labels),
  conditionValueUnits : mapConditionTypesToLabels(labels, {
    [CheckinConditionType.Voltage]  : 'voltageUnit',
    [CheckinConditionType.GpsSpeed] : 'gpsSpeedUnit'
  }),
  vehicleDiscoveryStates : labels?.vehicleDiscoveryStates || {},
  and                    : labels?.and || '',
  or                     : labels?.or || ''
});

/**
 * Renders the request metadata summary accordion for a selected CAN request.
 *
 * The accordion presents ESN, status, timestamps, and CAN request parameters
 * used to create the request before response frames are displayed in the grid.
 *
 * ```typescript
 * import CanRequestSummaryAccordion from
 *   '@ion/applications/src/pages/vehicle/manager/CanRequestSummaryAccordion';
 * ...
 *
 * <CanRequestSummaryAccordion requestDetails={requestDetails} />
 * ```
 *
 * @param   requestDetails The selected CAN request details payload.
 * @returns The request details summary accordion component.
 */
const CanRequestSummaryAccordion = ({
  requestDetails
}: CanRequestSummaryAccordionProps) => {
  const classes          = useStyles();
  const canRequest       = requestDetails?.canRequest || {};
  const metadataPrefix   = 'vehicle-manager:vehicleHistoryGrid.canRequestDetailsGrid.metadata';
  const canRequestPrefix = 'vehicle-manager:vehicleHistoryGrid.canRequestDetailsGrid.canRequest';
  const detailsPrefix    = 'vehicle-manager:vehicleHistoryGrid.canRequestDetailsGrid';

  const statusType = CAN_REQUEST_STATUS_ICON_MAP?.
    [requestDetails?.status as OfflineCanRequestStatus] ||
    IconStatus.validation;

  const checkinConditionLabels = translate(
    'vehicle-manager:vehicleHistoryGrid.canRequestCreateDialog.checkinConditions',
    {returnObjects: true}
  ) || {};

  const conditionsApplied = formatCheckinConditionsForDisplay(
    requestDetails?.condition,
    getCheckinConditionDisplayLabels(checkinConditionLabels)
  );

  const metaDataFields = [
    {
      key   : 'esn',
      label : translate(`${metadataPrefix}.esn`),
      value : requestDetails?.esn || ''
    },
    {
      key   : 'status',
      label : translate(`${metadataPrefix}.status`),
      value : (
        <StatusIndicator type={statusType}
          text={requestDetails?.status || ''} />
      )
    },
    {
      key   : 'createdOn',
      label : translate(`${metadataPrefix}.createdOn`),
      value : requestDetails?.createdOn ? formatDateTime(requestDetails.createdOn) : ''
    },
    {
      key   : 'updatedOn',
      label : translate(`${metadataPrefix}.updatedOn`),
      value : requestDetails?.updatedOn ? formatDateTime(requestDetails.updatedOn) : ''
    },
    {
      key   : 'label',
      label : translate(`${metadataPrefix}.label`),
      value : requestDetails?.label || ''
    }
  ];

  const requestPayloadFields = [
    {
      key   : 'txId',
      label : translate(`${canRequestPrefix}.txId`),
      value : formatCanNumberToHex(canRequest?.txId ?? '')
    },
    {
      key   : 'txLen',
      label : translate(`${canRequestPrefix}.txLen`),
      value : canRequest?.txLen ?? ''
    },
    {
      key   : 'rxId',
      label : translate(`${canRequestPrefix}.rxId`),
      value : formatCanNumberToHex(canRequest?.rxId ?? '')
    },
    {
      key   : 'rxMask',
      label : translate(`${canRequestPrefix}.rxMask`),
      value : formatCanNumberToHex(canRequest?.rxMask ?? '')
    },
    {
      key   : 'canSource',
      label : translate(`${canRequestPrefix}.canSource`),
      value : canRequest?.canSource ?? ''
    },
    {
      key   : 'canSpeed',
      label : translate(`${canRequestPrefix}.canSpeed`),
      value : canRequest?.canSpeed ?? ''
    },
    {
      key            : 'txPayload',
      label          : translate(`${canRequestPrefix}.txPayload`),
      value          : formatCanPayloadToHex(canRequest?.txPayload || ''),
      valueClassName : classes.payloadValue
    },
    ...(conditionsApplied ? [{
      key            : 'conditionsApplied',
      label          : translate(`${canRequestPrefix}.conditionsApplied`),
      value          : conditionsApplied,
      valueClassName : classes.payloadValue,
      itemClassName  : `${classes.sectionItem} ${classes.conditionsAppliedItem}`
    }] : [])
  ];

  return (
    <div className={classes.summaryAccordionWrapper}>
      <Accordion className={classes.summaryAccordion}
        backgroundColor={dlsColor('grey5')}
        padding='24px'
        margin='0'
        borderBottom='none'
        expanded={canRequestSummaryAccordionExpanded.value}
        onExpand={setCanRequestSummaryAccordionExpanded}
        header={(
          <div className={classes.summaryAccordionTitle}>
            {translate(`${detailsPrefix}.requestDetails`)}
          </div>
        )}
        details={(
          <Grid container
            spacing={0}
            alignItems='flex-start'
            justifyContent='flex-start'
            className={classes.metadataContainer}>
            {metaDataFields.map((field) => (
              <Grid item
                className={classes.metadataItem}
                key={field.key}>
                <div className={classes.metadataLabel}>
                  {field.label}
                </div>
                <div className={classes.metadataValue}>
                  {field.value}
                </div>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Divider className={classes.sectionDivider} />
            </Grid>
            <div className={classes.sectionContainer}>
              {requestPayloadFields.map((field) => (
                <div className={field.itemClassName || classes.sectionItem}
                  key={field.key}>
                  <div className={classes.sectionLabel}>
                    {field.label}
                  </div>
                  <div className={field.valueClassName || classes.sectionValue}>
                    {field.value}
                  </div>
                </div>
              ))}
            </div>
          </Grid>
        )} />
    </div>
  );
};

export default CanRequestSummaryAccordion;
